import { Form, Formik } from "formik";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { renderFormulaList } from "../../actions/formulaAction";
import {
  getCreatePrescriptionPatients,
  patientPostAction,
} from "../../actions/patientAction";
import {
  clearUnfinalizedPrescription,
  setUnfinalizedPrescriptionId,
} from "../../actions/prescriptionAction";
import FormikControl from "../../components/WraapperComponent/FormikControl";
import { states } from "../../constants/states";
import { getUserDetail, isEmpty } from "../../utils/helper";

const CreatePatientTab = ({
  setActiveSection,
  clearPrescriptionValue,
  setPatientID,
  setPatientName,
  setItem,
}) => {
  const dispatch = useDispatch();
  const appLoading = useSelector((state) => state.alert.appLoading);
  const patientFormFields = useSelector(
    (state) => state.settings.patientFormFields
  );
  const isRequired = false;
  const initialValues = {
    firstname: "",
    lastname: "",
    nickname: "",
    email: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    phone: "",
    zip: "",
    addressNickname: "",
    note: "",
  };

  const validationSchema = (patientFormFields) => {
    return Yup.object({
      firstname: Yup.string().nullable().required("This field is required"),
      lastname: Yup.string().nullable().required("This field is required"),
      email: Yup.string()
        .email()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Email")
              ?.isRequired === true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable(),
      phone: Yup.string()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Phone")
              ?.isRequired === true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable()
        .min(10, "Phone number must be 10 digits long")
        .max(10, "Phone number must be 10 digits long")
        .matches(
          /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
          "Phone number is not valid"
        ),

      address_1: Yup.string()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Address")
              ?.isRequired === true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable(),
      address_2: Yup.string().nullable(),
      city: Yup.string()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Address")
              ?.isRequired === true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable(),
      state: Yup.string()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Address")
              ?.isRequired === true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable(),
      zip: Yup.string()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Address")
              ?.isRequired === true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable()
        .max(5)
        .min(5)
        .matches(/^\d{5}$/, "Zip code is not valid"),
      addressNickname: Yup.string().nullable(),
      nickname: Yup.string()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Nickname")
              ?.isRequired === true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable(),
      note: Yup.string()
        .when([], {
          is: () =>
            patientFormFields.find((x) => x.fieldName == "Note")?.isRequired ===
            true,
          then: Yup.string().required("This field is required"),
          otherwise: Yup.string(),
        })
        .nullable(),
    });
  };

  const onSubmit = (values, { resetForm }) => {
    const data = {
      first_name: values.firstname,
      last_name: values.lastname,
      nick_name: values.nickname,
      address_1: values.address_1,
      address_2: values.address_2,
      city: values.city,
      state: values.state,
      phone_number: values.phone,
      zip: values.zip,
      address_nickname:
        values.addressNickname === "" ? null : values.addressNickname,
      email_address: values.email,
      notes: values.note,
      action: "practitionerCreatePatient",
      token: getUserDetail()?.token,
    };

    var params = {
      params: data,
    };
    dispatch(
      patientPostAction(params, (id) => {
        resetForm();
        if (setActiveSection) {
          dispatch(clearUnfinalizedPrescription());
          dispatch(setUnfinalizedPrescriptionId(0));
          clearPrescriptionValue();
          dispatch(renderFormulaList(true));
          dispatch(getCreatePrescriptionPatients());
          setActiveSection(2);
          setPatientID(id);
          setPatientName(`${values.firstname} ${values.lastname}`);
          setTimeout(() => {
            dispatch(setItem([1, 3]));
          }, 10);
        }
      })
    );
  };

  return (
    <div>
      <p className="page-desc">
        Enter Patient Information. (<span className="required">*</span> Required
        fields)
      </p>
      <div className="form-section">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(patientFormFields)}
          onSubmit={onSubmit}
        >
          {({ errors, touched, setFieldValue, handleBlur }) => {
            return (
              <Form>
                <div className="row">
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label="* First Name"
                      name="firstname"
                      handleBlur={handleBlur}
                      hasError={errors.firstname}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label="* Last Name"
                      name="lastname"
                      handleBlur={handleBlur}
                      hasError={errors.lastname && touched.lastname}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Nickname")
                          ?.isRequired === true
                          ? "* ID/Nickname"
                          : "ID/Nickname"
                      }
                      name="nickname"
                      handleBlur={handleBlur}
                      hasError={errors.nickname && touched.nickname}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Address")
                          ?.isRequired === true
                          ? "* street address"
                          : "street address"
                      }
                      name="address_1"
                      handleBlur={handleBlur}
                      hasError={errors.address_1 && touched.address_1}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label="apt/unit #"
                      name="address_2"
                      handleBlur={handleBlur}
                      hasError={errors.address_2 && touched.address_2}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label="Address Description"
                      name="addressNickname"
                      handleBlur={handleBlur}
                      hasError={
                        errors.addressNickname && touched.addressNickname
                      }
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Address")
                          ?.isRequired === true
                          ? "* City"
                          : "City"
                      }
                      name="city"
                      handleBlur={handleBlur}
                      hasError={errors.city && touched.city}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="dropdown"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Address")
                          ?.isRequired === true
                          ? "* State"
                          : "state"
                      }
                      name="state"
                      handleBlur={handleBlur}
                      hasError={errors.state && touched.state}
                      options={states}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Address")
                          ?.isRequired === true
                          ? "* Zip code"
                          : "Zip code"
                      }
                      name="zip"
                      handleBlur={handleBlur}
                      maxLength={5}
                      hasError={errors.zip && touched.zip}
                    />
                  </div>
                  <div className="col-sm-4">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Phone")
                          ?.isRequired === true
                          ? "* Phone"
                          : "Phone"
                      }
                      name="phone"
                      handleBlur={handleBlur}
                      maxLength={10}
                      onPaste={(e) => {
                        const str = e.clipboardData.getData("Text");
                        let newStr = str.replace(/[^0-9]/g, "");
                        if (newStr.length > 10 && newStr[0] == 1) {
                          newStr = newStr.slice(1);
                        }
                        if (str !== newStr) {
                          e.preventDefault();
                          setFieldValue("phone", newStr);
                        }
                      }}
                      hasError={errors.phone && touched.phone}
                    />
                  </div>

                  <div className="col-sm-8">
                    <FormikControl
                      control="forminput"
                      type="text"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Email")
                          ?.isRequired === true
                          ? "* Email"
                          : "Email"
                      }
                      name="email"
                      handleBlur={handleBlur}
                      hasError={errors.email && touched.email}
                    />
                  </div>
                  <div className="col-sm-12">
                    <FormikControl
                      control="textarea"
                      label={
                        patientFormFields.find((x) => x.fieldName == "Note")
                          ?.isRequired === true
                          ? "* NOTE: (This is a PRIVATE note that only you have access to. The patient will not see it.)"
                          : "NOTE: (This is a PRIVATE note that only you have access to. The patient will not see it.)"
                      }
                      name="note"
                      rows="4"
                      handleBlur={handleBlur}
                      hasError={errors.note && touched.note}
                    />
                  </div>
                </div>
                <div className="submit-section">
                  <button
                    className="btn-orange"
                    type="submit"
                    disabled={appLoading}
                  >
                    Add
                  </button>
                  <p className="page-desc">
                    Patient will be notified via email.
                  </p>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default CreatePatientTab;
